import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { AccountSelector } from "../components/portfolio/AccountSelector";
import { PortfolioStats } from "../components/portfolio/PortfolioStats";
import { StrategyCard } from "../components/portfolio/StrategyCard";
import { HoldingsTab } from "../components/portfolio/HoldingsTab";
import { PerformanceChart } from "../components/portfolio/PerformanceChart";
import { PerformanceReport } from "../components/portfolio/PerformanceReport";
import {
  getPortfolioAccounts,
  getPortfolioStrategies,
  type AccountSummary,
  type StrategyCard as StrategyCardType,
} from "../services/portfolioService";
import {
  previewInvestment,
  executeInvestment,
  liquidateInvestment,
  cancelInvestment,
  depositFunds,
  withdrawFunds,
  getExecutionRunStatus,
  type PreviewResult,
  type ExecutionRunStatus,
} from "../services/executionService";

export function PortfolioPageNew() {
  const navigate = useNavigate();
  
  // Data state
  const [accounts, setAccounts] = useState<AccountSummary[]>([]);
  const [strategies, setStrategies] = useState<StrategyCardType[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Summary data (strings from API)
  const [totalEquity, setTotalEquity] = useState("0.00");
  const [todayChange, setTodayChange] = useState("0.00");
  const [todayChangePct, setTodayChangePct] = useState("0.00");
  const [cashAvailable, setCashAvailable] = useState(0);
  const [buyingPower, setBuyingPower] = useState(0);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<"strategies" | "holdings" | "performance">("strategies");
  
  // Expanded strategy
  const [expandedStrategyId, setExpandedStrategyId] = useState<string | null>(null);
  
  // Preview modal
  const [previewData, setPreviewData] = useState<PreviewResult | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  
  // Execute modal
  const [executeInvestmentId, setExecuteInvestmentId] = useState<string | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executeResult, setExecuteResult] = useState<string | null>(null);
  
  // Liquidate modal
  const [liquidateInvestmentId, setLiquidateInvestmentId] = useState<string | null>(null);
  const [isLiquidating, setIsLiquidating] = useState(false);
  const [liquidateError, setLiquidateError] = useState<string | null>(null);
  
  // Cancel modal (for non-executed investments)
  const [cancelInvestmentId, setCancelInvestmentId] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  
  // Refs for polling cleanup on unmount
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  
  // Cleanup polling on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);
  
  // Deposit modal
  const [depositInvestmentId, setDepositInvestmentId] = useState<string | null>(null);
  const [depositAmount, setDepositAmount] = useState("");
  const [isDepositing, setIsDepositing] = useState(false);
  const [depositError, setDepositError] = useState<string | null>(null);
  
  // Withdraw modal
  const [withdrawInvestmentId, setWithdrawInvestmentId] = useState<string | null>(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [accountsData, strategiesData] = await Promise.all([
        getPortfolioAccounts(), // Don't filter accounts - always show all
        getPortfolioStrategies(selectedAccountId), // Filter strategies by selected account
      ]);
      
      setAccounts(accountsData.accounts || []);
      
      // If a specific account is selected, use that account's Alpaca stats
      // Otherwise use aggregated totals across all accounts
      if (selectedAccountId) {
        // Find the selected account and use its stats from Alpaca
        const selectedAccount = accountsData.accounts?.find(acc => acc.id === selectedAccountId);
        if (selectedAccount) {
          setTotalEquity(selectedAccount.portfolio_value);
          setTodayChange(selectedAccount.today_change);
          setTodayChangePct(selectedAccount.today_change_pct);
          // Cash comes directly from the selected account's Alpaca data
          setCashAvailable(parseFloat(selectedAccount.cash || "0"));
          setBuyingPower(parseFloat(selectedAccount.buying_power || "0"));
        }
      } else {
        // Use aggregated data from all accounts
        setTotalEquity(accountsData.total_equity || "0.00");
        setTodayChange(accountsData.today_change || "0.00");
        setTodayChangePct(accountsData.today_change_pct || "0.00");
        // Cash from aggregated total (sum of all accounts)
        setCashAvailable(parseFloat(accountsData.total_cash || "0"));
        setBuyingPower(parseFloat(accountsData.total_buying_power || "0"));
      }
      
      setStrategies(strategiesData.strategies || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load portfolio data");
    } finally {
      setIsLoading(false);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter strategies by selected account and sort (New first, Live last)
  const filteredStrategies = (selectedAccountId
    ? strategies.filter((s) => s.brokerage_account_id === selectedAccountId)
    : strategies
  ).sort((a, b) => {
    // New strategies (has_executed = false) first
    if (!a.has_executed && b.has_executed) return -1;
    if (a.has_executed && !b.has_executed) return 1;
    // Within same status, sort by started_at (newest first)
    return new Date(b.started_at).getTime() - new Date(a.started_at).getTime();
  });

  // Get investment IDs for performance chart (only executed strategies)
  const executedInvestmentIds = useMemo(() => {
    return filteredStrategies
      .filter((s) => s.has_executed)
      .map((s) => s.id);
  }, [filteredStrategies]);

  // Handle preview
  const handlePreview = async (investmentId: string) => {
    setIsPreviewLoading(true);
    setPreviewError(null);
    try {
      const data = await previewInvestment(investmentId);
      setPreviewData(data);
    } catch (err) {
      console.error("Preview failed:", err);
      setPreviewError(err instanceof Error ? err.message : "Preview failed");
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Handle execute
  const handleExecute = (investmentId: string) => {
    setExecuteInvestmentId(investmentId);
    setExecuteResult(null);
  };

  const confirmExecute = async () => {
    if (!executeInvestmentId) return;
    setIsExecuting(true);
    try {
      const asyncResp = await executeInvestment(executeInvestmentId, false);
      const runId = asyncResp.execution_run_id;
      setExecuteResult(`Executing... (run ${runId.slice(0, 8)})`);

      // Poll for completion every 2 seconds
      const pollInterval = 2000;
      const maxPolls = 150; // 5 min max
      let polls = 0;

      const poll = async (): Promise<void> => {
        if (!mountedRef.current) return; // Stop if unmounted
        polls++;
        try {
          const status: ExecutionRunStatus = await getExecutionRunStatus(runId);

          if (!mountedRef.current) return; // Check again after async

          // Update UI with progress
          const phaseLabel: Record<string, string> = {
            pending: "Starting...",
            calculating: "Calculating trades...",
            executing_sells: `Executing sells (${status.orders_filled}/${status.total_orders || "?"} filled)...`,
            executing_buys: `Executing buys (${status.orders_filled}/${status.total_orders || "?"} filled)...`,
            updating_shadow: "Updating positions...",
          };
          if (!["completed", "partial_failure", "failed"].includes(status.status)) {
            setExecuteResult(phaseLabel[status.status] || `Status: ${status.status}`);
          }

          if (status.status === "completed") {
            setExecuteResult(
              `Success! ${status.orders_submitted} orders submitted, ${status.orders_filled} filled` +
                (status.duration_ms ? ` (${(status.duration_ms / 1000).toFixed(1)}s)` : "")
            );
            await loadData();
            setIsExecuting(false);
            return;
          } else if (status.status === "partial_failure") {
            setExecuteResult(
              `Partial: ${status.orders_filled} filled, ${status.orders_failed} failed. ${status.error_message || ""}`
            );
            await loadData();
            setIsExecuting(false);
            return;
          } else if (status.status === "failed") {
            setExecuteResult(`Failed: ${status.error_message || "Unknown error"}`);
            setIsExecuting(false);
            return;
          }

          if (polls >= maxPolls) {
            setExecuteResult("Execution is still running. Check back later.");
            setIsExecuting(false);
            return;
          }

          // Continue polling
          pollTimerRef.current = setTimeout(poll, pollInterval);
        } catch (pollErr) {
          if (!mountedRef.current) return;
          console.error("Poll error:", pollErr);
          setExecuteResult("Execution started but status check failed. Refresh to see results.");
          setIsExecuting(false);
        }
      };

      // Start polling after a short delay
      pollTimerRef.current = setTimeout(poll, pollInterval);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Execution failed";
      // Handle 409 Conflict (execution already in progress)
      if (msg.includes("already in progress") || msg.includes("409")) {
        setExecuteResult("Execution already in progress for this strategy. Please wait.");
      } else {
        setExecuteResult(msg);
      }
      setIsExecuting(false);
    }
  };

  // Handle liquidate
  const handleLiquidate = (investmentId: string) => {
    setLiquidateInvestmentId(investmentId);
  };

  const confirmLiquidate = async () => {
    if (!liquidateInvestmentId) return;
    setIsLiquidating(true);
    setLiquidateError(null);
    try {
      const asyncResp = await liquidateInvestment(liquidateInvestmentId);
      const runId = asyncResp.execution_run_id;

      // Poll for completion
      const pollInterval = 2000;
      const maxPolls = 150;
      let polls = 0;

      const poll = async (): Promise<void> => {
        if (!mountedRef.current) return;
        polls++;
        try {
          const status: ExecutionRunStatus = await getExecutionRunStatus(runId);

          if (!mountedRef.current) return;

          if (status.status === "completed") {
            await loadData();
            setLiquidateInvestmentId(null);
            setIsLiquidating(false);
            return;
          } else if (status.status === "partial_failure" || status.status === "failed") {
            setLiquidateError(status.error_message || "Liquidation failed");
            setIsLiquidating(false);
            return;
          }

          if (polls >= maxPolls) {
            setLiquidateError("Liquidation still running. Check back later.");
            setIsLiquidating(false);
            return;
          }

          pollTimerRef.current = setTimeout(poll, pollInterval);
        } catch (pollErr) {
          if (!mountedRef.current) return;
          console.error("Liquidation poll error:", pollErr);
          setLiquidateError("Liquidation started but status check failed.");
          setIsLiquidating(false);
        }
      };

      pollTimerRef.current = setTimeout(poll, pollInterval);
    } catch (err) {
      console.error("Liquidate failed:", err);
      const msg = err instanceof Error ? err.message : "Failed to liquidate investment";
      if (msg.includes("already in progress") || msg.includes("409")) {
        setLiquidateError("Execution already in progress. Please wait.");
      } else {
        setLiquidateError(msg.replace("Failed to liquidate: ", ""));
      }
      setIsLiquidating(false);
    }
  };

  // Handle cancel (for non-executed investments)
  const handleCancel = (investmentId: string) => {
    setCancelInvestmentId(investmentId);
  };

  const confirmCancel = async () => {
    if (!cancelInvestmentId) return;
    setIsCancelling(true);
    setCancelError(null);
    try {
      await cancelInvestment(cancelInvestmentId);
      await loadData();
      setCancelInvestmentId(null);
    } catch (err) {
      console.error("Cancel failed:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to cancel investment";
      setCancelError(errorMessage.replace("Failed to cancel: ", ""));
    } finally {
      setIsCancelling(false);
    }
  };

  // Handle deposit
  const handleDeposit = (investmentId: string) => {
    setDepositInvestmentId(investmentId);
    setDepositAmount("");
    setDepositError(null);
  };

  const confirmDeposit = async () => {
    if (!depositInvestmentId || !depositAmount) return;
    const amount = parseFloat(depositAmount);
    if (isNaN(amount) || amount <= 0) {
      setDepositError("Please enter a valid amount");
      return;
    }
    
    // Validate minimum $100 after pending_delta
    const strategy = strategies.find(s => s.id === depositInvestmentId);
    if (strategy) {
      const currentValue = parseFloat(strategy.current_value || "0");
      const pendingDelta = strategy.pending_delta || 0;
      const projectedValue = currentValue + pendingDelta + amount;
      
      if (projectedValue < 100) {
        setDepositError(`Strategy value after deposit would be $${projectedValue.toFixed(2)}, minimum is $100.00`);
        return;
      }
    }
    
    setIsDepositing(true);
    try {
      await depositFunds(depositInvestmentId, amount);
      await loadData();
      setDepositInvestmentId(null);
    } catch (err) {
      setDepositError(err instanceof Error ? err.message : "Failed to deposit");
    } finally {
      setIsDepositing(false);
    }
  };

  // Handle withdraw
  const handleWithdraw = (investmentId: string) => {
    setWithdrawInvestmentId(investmentId);
    setWithdrawAmount("");
    setWithdrawError(null);
  };

  const confirmWithdraw = async () => {
    if (!withdrawInvestmentId || !withdrawAmount) return;
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) {
      setWithdrawError("Please enter a valid amount");
      return;
    }
    
    // Validate withdrawal against current_value
    // User can withdraw up to current_value - $100 minimum
    const strategy = strategies.find(s => s.id === withdrawInvestmentId);
    if (strategy) {
      const currentValue = parseFloat(strategy.current_value) || 0;
      const pendingDelta = strategy.pending_delta || 0;
      const projectedValue = currentValue + pendingDelta - amount;
      
      if (projectedValue < 100) {
        setWithdrawError(`Value after withdrawal would be $${projectedValue.toFixed(2)}, minimum is $100.00`);
        return;
      }
    }
    
    setIsWithdrawing(true);
    try {
      await withdrawFunds(withdrawInvestmentId, amount);
      await loadData();
      setWithdrawInvestmentId(null);
    } catch (err) {
      setWithdrawError(err instanceof Error ? err.message : "Failed to withdraw");
    } finally {
      setIsWithdrawing(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div className="bg-gradient-to-b from-surface-950 to-surface-900">
      {/* Header */}
      <PageHeader
        title="Portfolio"
        subtitle="Track your investments and manage your holdings"
        backgroundImage="https://images.unsplash.com/photo-1642790106117-e829e14a795f?w=1920&q=80"
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-8 -mt-8 relative z-10">
        {/* Error State */}
        {error && (
          <div className="mb-6 glass-card border-red-500/30 bg-red-500/10 p-4">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-300">{error}</span>
              <button onClick={loadData} className="ml-auto text-sm text-red-400 hover:text-red-300">
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="spinner w-10 h-10 mb-4" />
            <p className="text-surface-400">Loading your portfolio...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Account Selector + Stats Row */}
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Account Selector */}
              <div className="lg:w-72">
                <AccountSelector
                  accounts={accounts}
                  selectedAccountId={selectedAccountId ?? null}
                  onSelectAccount={(id) => setSelectedAccountId(id ?? undefined)}
                  totalEquity={totalEquity}
                  todayChange={todayChange}
                  todayChangePct={todayChangePct}
                />
              </div>
              
              {/* Portfolio Stats */}
              <div className="flex-1">
                <PortfolioStats
                  portfolioValue={totalEquity}
                  todayChange={todayChange}
                  cashBalance={cashAvailable}
                  buyingPower={buyingPower}
                />
              </div>
            </div>

            {/* Performance Chart - only show if we have executed strategies */}
            {executedInvestmentIds.length > 0 && (
              <PerformanceChart investmentIds={executedInvestmentIds} />
            )}

            {/* Tabs */}
            <div className="glass-card p-1 inline-flex rounded-xl">
              <button
                onClick={() => setActiveTab("strategies")}
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === "strategies"
                    ? "bg-primary-500/20 text-primary-400 shadow-lg shadow-primary-500/10"
                    : "text-surface-400 hover:text-surface-200 hover:bg-surface-700/50"
                }`}
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  Strategies
                  <span className="px-2 py-0.5 rounded-full bg-surface-700 text-xs">
                    {filteredStrategies.length}
                  </span>
                </span>
              </button>
              <button
                onClick={() => setActiveTab("holdings")}
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === "holdings"
                    ? "bg-primary-500/20 text-primary-400 shadow-lg shadow-primary-500/10"
                    : "text-surface-400 hover:text-surface-200 hover:bg-surface-700/50"
                }`}
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  Holdings
                </span>
              </button>
              <button
                onClick={() => setActiveTab("performance")}
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === "performance"
                    ? "bg-primary-500/20 text-primary-400 shadow-lg shadow-primary-500/10"
                    : "text-surface-400 hover:text-surface-200 hover:bg-surface-700/50"
                }`}
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                  </svg>
                  Performance
                </span>
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === "strategies" ? (
              <div className="space-y-4">
                {filteredStrategies.length === 0 ? (
                  <div className="glass-card p-12 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-surface-800 flex items-center justify-center">
                      <svg className="w-8 h-8 text-surface-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-surface-200 mb-2">No Strategies Yet</h3>
                    <p className="text-surface-400 mb-6 max-w-md mx-auto">
                      Start by investing in a strategy to track your portfolio performance.
                    </p>
                    <button
                      onClick={() => navigate("/search")}
                      className="btn btn-primary"
                    >
                      Community Strategies
                    </button>
                  </div>
                ) : (
                  filteredStrategies.map((strategy) => (
                    <StrategyCard
                      key={strategy.id}
                      strategy={strategy}
                      isExpanded={expandedStrategyId === strategy.id}
                      onToggleExpand={() => 
                        setExpandedStrategyId(
                          expandedStrategyId === strategy.id ? null : strategy.id
                        )
                      }
                      onPreview={() => handlePreview(strategy.id)}
                      onExecute={() => handleExecute(strategy.id)}
                      onDeposit={() => handleDeposit(strategy.id)}
                      onWithdraw={() => handleWithdraw(strategy.id)}
                      onLiquidate={() => handleLiquidate(strategy.id)}
                      onCancel={() => handleCancel(strategy.id)}
                      onPerformance={() => navigate(`/backtest/${strategy.strategy_id}`)}
                    />
                  ))
                )}
              </div>
            ) : activeTab === "holdings" ? (
              <HoldingsTab accountId={selectedAccountId} />
            ) : (
              <PerformanceReport accountId={selectedAccountId} />
            )}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {(previewData || isPreviewLoading || previewError) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="glass-card max-w-2xl w-full max-h-[80vh] overflow-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-surface-100">Trade Preview</h3>
              <button
                onClick={() => { setPreviewData(null); setPreviewError(null); }}
                className="p-2 rounded-lg hover:bg-surface-700 transition-colors"
              >
                <svg className="w-5 h-5 text-surface-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {isPreviewLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner w-8 h-8" />
              </div>
            ) : previewError ? (
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <h4 className="font-medium text-red-400">Preview Failed</h4>
                    <p className="text-sm text-red-300 mt-1">{previewError}</p>
                  </div>
                </div>
              </div>
            ) : previewData ? (
              <div className="space-y-4">
                {/* Stale Data Warning Banner */}
                {previewData.stale_data_warning && (
                  <div className="stale-warning p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                    <div className="flex items-start gap-3">
                      <svg className="stale-warning-icon w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <h4 className="stale-warning-title font-medium text-yellow-400">Stale Allocation Data</h4>
                        <p className="stale-warning-message text-sm text-yellow-300/80 mt-1">
                          {previewData.stale_data_warning.message}
                        </p>
                        <p className="stale-warning-detail text-xs text-yellow-300/60 mt-2">
                          Allocation Date: {previewData.stale_data_warning.allocation_date} • Expected: {previewData.stale_data_warning.expected_date}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary info */}
                <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-surface-800/30">
                  <div>
                    <div className="text-xs text-surface-400">Portfolio Value</div>
                    <div className="text-surface-100">{formatCurrency(parseFloat(previewData.portfolio_value || "0"))}</div>
                  </div>
                  <div>
                    <div className="text-xs text-surface-400">Allocated</div>
                    <div className="text-surface-100">{formatCurrency(parseFloat(previewData.allocated_value || "0"))}</div>
                  </div>
                  <div>
                    <div className="text-xs text-surface-400">Cash Balance</div>
                    <div className="text-surface-100">{formatCurrency(parseFloat(previewData.cash_balance || "0"))}</div>
                  </div>
                  <div>
                    <div className="text-xs text-surface-400">First Execution?</div>
                    <div className="text-surface-100">{previewData.is_first_execution ? "Yes" : "No"}</div>
                  </div>
                </div>
                
                {/* Intended Trades */}
                <h4 className="text-sm font-medium text-surface-300 mt-4">Target Positions</h4>
                {previewData.intended_trades?.map((trade, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-surface-800/50">
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        trade.action === "BUY" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
                      }`}>
                        {trade.action}
                      </span>
                      <span className="font-medium text-surface-200">{trade.symbol}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-surface-200">
                        {parseFloat(trade.shares).toFixed(4).replace(/\.?0+$/, '')} shares
                      </div>
                      <div className="text-xs text-surface-400">
                        {parseFloat(trade.current_shares || "0").toFixed(2).replace(/\.?0+$/, '')} → {parseFloat(trade.target_shares || "0").toFixed(2).replace(/\.?0+$/, '')} ({(parseFloat(trade.target_weight || "0") * 100).toFixed(1)}%)
                      </div>
                    </div>
                  </div>
                ))}
                {(!previewData.intended_trades || previewData.intended_trades.length === 0) && (
                  <div className="text-center py-8 text-surface-400">
                    No trades needed - portfolio is already balanced
                  </div>
                )}

                {/* Skipped Allocations Warning */}
                {previewData.skipped_allocations && previewData.skipped_allocations.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-yellow-400 mb-2">
                      ⚠ Skipped Allocations ({previewData.skipped_allocations.length})
                    </h4>
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 space-y-2">
                      <p className="text-xs text-yellow-300/70">
                        These tickers were in the backtest target but could not be traded due to fractionability constraints.
                        Their weight has been redistributed to other positions.
                      </p>
                      {previewData.skipped_allocations.map((skipped, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded bg-yellow-500/5">
                          <div className="flex items-center gap-3">
                            <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400">
                              SKIP
                            </span>
                            <span className="font-medium text-surface-200">{skipped.symbol}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-surface-300">
                              Target: {(skipped.target_weight * 100).toFixed(1)}% (${parseFloat(skipped.target_value).toFixed(2)})
                            </div>
                            <div className="text-xs text-yellow-400/80">
                              Price: ${parseFloat(skipped.price).toFixed(2)} — can't buy 1 share
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Total Trade Volume */}
                {previewData.intended_trades && previewData.intended_trades.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-surface-700">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-surface-800/80">
                      <span className="font-medium text-surface-200">Total Trade Volume</span>
                      <span className="text-lg font-semibold text-surface-100">
                        {formatCurrency(
                          previewData.intended_trades.reduce((sum, trade) => {
                            const tradeShares = parseFloat(trade.shares || "0");
                            const estPrice = parseFloat(trade.est_price || "0");
                            return sum + (tradeShares * estPrice);
                          }, 0)
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Execute Confirmation */}
      <ConfirmDialog
        isOpen={!!executeInvestmentId}
        title="Execute Strategy"
        message={executeResult || "Are you sure you want to execute this strategy now? This will place real trades."}
        confirmText={executeResult ? "Close" : isExecuting ? "Executing..." : "Execute"}
        cancelText="Cancel"
        onConfirm={executeResult ? () => setExecuteInvestmentId(null) : confirmExecute}
        onClose={() => setExecuteInvestmentId(null)}
        variant={executeResult?.includes("Success") ? "info" : executeResult ? "danger" : "warning"}
      />

      {/* Liquidate Confirmation */}
      <ConfirmDialog
        isOpen={!!liquidateInvestmentId}
        title="Liquidate Investment"
        message={liquidateError || "Are you sure you want to liquidate this investment? This will sell all positions and close the investment."}
        confirmText={liquidateError ? "Close" : isLiquidating ? "Liquidating..." : "Liquidate"}
        cancelText={liquidateError ? undefined : "Cancel"}
        onConfirm={liquidateError ? () => {
          setLiquidateInvestmentId(null);
          setLiquidateError(null);
        } : confirmLiquidate}
        onClose={() => {
          setLiquidateInvestmentId(null);
          setLiquidateError(null);
        }}
        variant={liquidateError ? "danger" : "warning"}
      />

      {/* Cancel Confirmation (for non-executed investments) */}
      <ConfirmDialog
        isOpen={!!cancelInvestmentId}
        title="Cancel Investment"
        message={cancelError || "Are you sure you want to cancel this investment? The invested amount will be returned to your portfolio cash balance."}
        confirmText={cancelError ? "Close" : isCancelling ? "Cancelling..." : "Cancel Investment"}
        cancelText={cancelError ? undefined : "Keep"}
        onConfirm={cancelError ? () => {
          setCancelInvestmentId(null);
          setCancelError(null);
        } : confirmCancel}
        onClose={() => {
          setCancelInvestmentId(null);
          setCancelError(null);
        }}
        variant={cancelError ? "danger" : "warning"}
      />

      {/* Deposit Modal */}
      {depositInvestmentId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="glass-card max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">Add Funds</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-surface-400 mb-2">Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">$</span>
                  <input
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 rounded-lg bg-surface-800 border border-surface-700 text-surface-100 focus:outline-none focus:border-primary-500"
                    placeholder="0.00"
                  />
                </div>
                {depositError && (
                  <p className="text-red-400 text-sm mt-2">{depositError}</p>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setDepositInvestmentId(null)}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-surface-700 text-surface-300 hover:bg-surface-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeposit}
                  disabled={isDepositing}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-green-600 text-white hover:bg-green-500 transition-colors disabled:opacity-50"
                >
                  {isDepositing ? "Adding..." : "Add Funds"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      {withdrawInvestmentId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="glass-card max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-surface-100 mb-4">Withdraw Funds</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-surface-400 mb-2">Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400">$</span>
                  <input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 rounded-lg bg-surface-800 border border-surface-700 text-surface-100 focus:outline-none focus:border-primary-500"
                    placeholder="0.00"
                  />
                </div>
                {withdrawError && (
                  <p className="text-red-400 text-sm mt-2">{withdrawError}</p>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setWithdrawInvestmentId(null)}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-surface-700 text-surface-300 hover:bg-surface-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmWithdraw}
                  disabled={isWithdrawing}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-amber-600 text-white hover:bg-amber-500 transition-colors disabled:opacity-50"
                >
                  {isWithdrawing ? "Withdrawing..." : "Withdraw"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
